/**
 * Payment Link Error Handler Utility
 *
 * This utility provides centralized error handling for payment link API responses.
 * It maps backend error codes to appropriate frontend actions (routing, display messages, etc.)
 *
 * Security consideration: All errors return HTTP 404 from backend to prevent enumeration attacks.
 * Error codes in response body help frontend show appropriate user messages.
 */

// Error codes yang match dengan backend enum PaymentLinkErrorCode
export const PaymentLinkErrorCode = {
  // Payment Link Errors
  PAYMENT_LINK_NOT_FOUND: 'PAYMENT_LINK_NOT_FOUND',
  PAYMENT_LINK_EXPIRED: 'PAYMENT_LINK_EXPIRED',
  PAYMENT_LINK_FULFILLED: 'PAYMENT_LINK_FULFILLED',
  PAYMENT_LINK_CLOSED: 'PAYMENT_LINK_CLOSED',

  // Payment Link History Errors
  PAYMENT_HISTORY_NOT_FOUND: 'PAYMENT_HISTORY_NOT_FOUND',
  PAYMENT_HISTORY_EXPIRED: 'PAYMENT_HISTORY_EXPIRED',
  PAYMENT_HISTORY_PAID: 'PAYMENT_HISTORY_PAID',
  PAYMENT_HISTORY_FAILED: 'PAYMENT_HISTORY_FAILED',

  // Session Errors
  SESSION_LOST: 'SESSION_LOST',
};

/**
 * Determine if error code should show expired page
 */
export const isExpiredError = (errorCode) => {
  return [
    PaymentLinkErrorCode.PAYMENT_LINK_EXPIRED,
    PaymentLinkErrorCode.PAYMENT_HISTORY_EXPIRED,
  ].includes(errorCode);
};

/**
 * Determine if error code should show paid/success page
 */
export const isPaidError = (errorCode) => {
  return errorCode === PaymentLinkErrorCode.PAYMENT_HISTORY_PAID;
};

/**
 * Determine if error code should show not found page
 */
export const isNotFoundError = (errorCode) => {
  return [
    PaymentLinkErrorCode.PAYMENT_LINK_NOT_FOUND,
    PaymentLinkErrorCode.PAYMENT_LINK_FULFILLED,
    PaymentLinkErrorCode.PAYMENT_LINK_CLOSED,
    PaymentLinkErrorCode.PAYMENT_HISTORY_NOT_FOUND,
    PaymentLinkErrorCode.PAYMENT_HISTORY_FAILED,
  ].includes(errorCode);
};

/**
 * Get user-friendly error message for display
 */
export const getErrorMessage = (errorCode) => {
  const messages = {
    [PaymentLinkErrorCode.PAYMENT_LINK_NOT_FOUND]: 'Payment link tidak ditemukan atau tidak tersedia. Pastikan link yang Anda gunakan benar.',
    [PaymentLinkErrorCode.PAYMENT_LINK_EXPIRED]: 'Payment link telah melewati batas waktu yang ditentukan. Masa berlaku link ini sudah habis.',
    [PaymentLinkErrorCode.PAYMENT_LINK_FULFILLED]: 'Payment link ini sudah penuh. Batas maksimum jumlah transaksi telah tercapai.',
    [PaymentLinkErrorCode.PAYMENT_LINK_CLOSED]: 'Payment link ini sudah ditutup dan tidak lagi menerima pembayaran.',
    [PaymentLinkErrorCode.PAYMENT_HISTORY_NOT_FOUND]: 'Transaksi pembayaran tidak ditemukan. Pastikan referensi nomor yang Anda gunakan benar.',
    [PaymentLinkErrorCode.PAYMENT_HISTORY_EXPIRED]: 'Transaksi pembayaran telah melewati batas waktu pembayaran.',
    [PaymentLinkErrorCode.PAYMENT_HISTORY_PAID]: 'Pembayaran untuk transaksi ini sudah selesai diproses.',
    [PaymentLinkErrorCode.PAYMENT_HISTORY_FAILED]: 'Transaksi pembayaran gagal diproses.',
    [PaymentLinkErrorCode.SESSION_LOST]: 'Oops! Sesi anda terputus, silahkan coba kembali.',
  };

  return messages[errorCode] || 'Terjadi kesalahan yang tidak terduga. Silakan coba lagi atau hubungi customer service.';
};

/**
 * Determine if error code is for fulfilled/max limit
 */
export const isFulfilledError = (errorCode) => {
  return errorCode === PaymentLinkErrorCode.PAYMENT_LINK_FULFILLED;
};

/**
 * Determine if error code is for closed payment link
 */
export const isClosedError = (errorCode) => {
  return errorCode === PaymentLinkErrorCode.PAYMENT_LINK_CLOSED;
};

/**
 * Determine if error code is for lost/expired session
 */
export const isSessionLostError = (errorCode) => {
  return errorCode === PaymentLinkErrorCode.SESSION_LOST;
};

/**
 * Get error title for display
 */
export const getErrorTitle = (errorCode) => {
  if (isExpiredError(errorCode)) {
    return 'Payment Link Kadaluarsa';
  }
  if (isPaidError(errorCode)) {
    return 'Pembayaran Selesai';
  }
  if (isFulfilledError(errorCode)) {
    return 'Payment Link Penuh';
  }
  if (isClosedError(errorCode)) {
    return 'Payment Link Ditutup';
  }
  if (isSessionLostError(errorCode)) {
    return 'Sesi Terputus';
  }
  return 'Transaksi Tidak Ditemukan';
};

/**
 * Parse error from API response
 * Extracts error_code from response, handles both JSON response and error objects
 */
export const parseApiError = async (response) => {
  try {
    // If response is already parsed
    if (response.error_code) {
      return {
        errorCode: response.error_code,
        message: response.message || getErrorMessage(response.error_code),
        httpStatus: response.code || 404,
        paymentData: response.payment_data || null,
      };
    }

    // If response is a fetch Response object
    if (response instanceof Response) {
      const data = await response.json();
      return {
        errorCode: data.error_code || null,
        message: data.message || 'An error occurred',
        httpStatus: response.status,
        paymentData: data.payment_data || null,
      };
    }

    // Fallback
    return {
      errorCode: null,
      message: response.message || 'An error occurred',
      httpStatus: 500,
      paymentData: null,
    };
  } catch (error) {
    return {
      errorCode: null,
      message: 'Failed to parse error response',
      httpStatus: 500,
      paymentData: null,
    };
  }
};

/**
 * Get navigation path based on error code
 * Returns the path to redirect user based on error type
 */
export const getErrorNavigationPath = (errorCode) => {
  if (isExpiredError(errorCode)) {
    return '/payment-expired';
  }
  if (isPaidError(errorCode)) {
    return '/payment-success';
  }
  // Default to showing generic error component (not navigating)
  return null;
};

/**
 * Handle API error and return structured error object
 * Use this as the main entry point for error handling
 *
 * @param {Response|Object} errorResponse - API response or error object
 * @returns {Promise<Object>} Structured error object with errorCode, message, shouldNavigate, navigationPath, paymentData
 */
export const handlePaymentLinkError = async (errorResponse) => {
  const parsedError = await parseApiError(errorResponse);
  const navigationPath = getErrorNavigationPath(parsedError.errorCode);

  return {
    errorCode: parsedError.errorCode,
    message: parsedError.message || getErrorMessage(parsedError.errorCode),
    title: getErrorTitle(parsedError.errorCode),
    httpStatus: parsedError.httpStatus,
    shouldNavigate: !!navigationPath,
    navigationPath: navigationPath,
    isExpired: isExpiredError(parsedError.errorCode),
    isPaid: isPaidError(parsedError.errorCode),
    isFulfilled: isFulfilledError(parsedError.errorCode),
    isClosed: isClosedError(parsedError.errorCode),
    isNotFound: isNotFoundError(parsedError.errorCode),
    paymentData: parsedError.paymentData,
  };
};

/**
 * Create error state data for navigation
 * Formats error information for passing to error pages via router state
 */
export const createErrorNavigationState = (errorInfo, additionalData = {}) => {
  return {
    errorCode: errorInfo.errorCode,
    message: errorInfo.message,
    title: errorInfo.title,
    ...additionalData,
  };
};
